/**
 * Keeps the OneTrust consent surface reachable by keyboard. The page can keep
 * its initial focus, but the next Tab/Shift+Tab press moves focus into the
 * banner. Once focus lands inside the consent surface, Tab from the last
 * visible control wraps to the first and Shift+Tab from the first wraps to the
 * last.
 */
export function applyOneTrustFocusTrap() {
  const trapTarget = document.querySelector<HTMLElement>(OT_CONSENT_CONTAINER);

  if (!trapTarget || trapTarget.hasAttribute(TRAP_APPLIED_ATTRIBUTE)) return;

  trapTarget.setAttribute(TRAP_APPLIED_ATTRIBUTE, 'true');
  trapTarget.addEventListener('click', handleConsentClick);

  applyDocumentListeners();
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function applyDocumentListeners() {
  if (consentSurfaceState.hasDocumentListeners) return;

  document.addEventListener('keydown', handleKeyDown, true);
  document.addEventListener('focusin', handleFocusIn);

  consentSurfaceState.hasDocumentListeners = true;
}

function handleKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    handleEscapeKeyDown(event);

    return;
  }

  if (event.key !== 'Tab') return;

  const trapTarget = getActiveTrapTarget();

  if (!trapTarget) return;

  const focusableElements = getVisibleFocusableElements(trapTarget);

  if (focusableElements.length === 0) return;

  const activeElement = document.activeElement;
  const isValidActiveElement = activeElement instanceof HTMLElement;
  const isInsideTarget = trapTarget.contains(activeElement);
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  if (!firstFocusable || !lastFocusable) return;

  if (!isValidActiveElement || !isInsideTarget) {
    event.preventDefault();

    rememberFocusedElement(activeElement);
    focusEdgeElement({ event, firstFocusable, lastFocusable });
    applyBackgroundContentInert();

    return;
  }

  if (checkIfPreferenceCenterTrapTarget(trapTarget)) {
    handlePreferenceCenterTabKeyDown({
      activeElement,
      event,
      firstFocusable,
      focusableElements,
      lastFocusable,
    });

    return;
  }

  const nextFocusable = getNextFocusableElement({
    activeElement,
    firstFocusable,
    focusableElements,
    isShiftKeyPressed: event.shiftKey,
    lastFocusable,
  });

  if (!nextFocusable) return;

  event.preventDefault();
  nextFocusable.focus();
}

function handleEscapeKeyDown(event: KeyboardEvent) {
  const trapTarget = document.querySelector<HTMLElement>(OT_CONSENT_CONTAINER);

  if (!trapTarget) return;

  const preferenceCenterCloseButton = getVisiblePreferenceCenterCloseButton();

  if (preferenceCenterCloseButton) {
    event.preventDefault();
    preferenceCenterCloseButton.click();

    return;
  }

  const didClose = closeOneTrust();

  if (!didClose) return;

  event.preventDefault();

  restoreWhenConsentSurfaceCloses(trapTarget);
}

function handleFocusIn(event: FocusEvent) {
  const target = event.target;
  const trapTarget = document.querySelector<HTMLElement>(OT_CONSENT_CONTAINER);

  if (!trapTarget || !(target instanceof HTMLElement)) return;

  if (!trapTarget.contains(target)) return;

  applyBackgroundContentInert();
}

function handleConsentClick(event: MouseEvent) {
  const target = event.target;

  if (!(target instanceof HTMLElement)) return;

  const closeButton = target.closest<HTMLElement>(CLOSE_BUTTON_SELECTOR);

  if (!closeButton) return;

  if (checkIfPreferenceCenterCloseButton(closeButton)) return;

  const trapTarget = document.querySelector<HTMLElement>(OT_CONSENT_CONTAINER);

  if (!trapTarget) return;

  restoreWhenConsentSurfaceCloses(trapTarget);
}

function closeOneTrust() {
  const oneTrust = getOneTrust();

  if (!oneTrust?.Close) {
    return false;
  }

  oneTrust.Close();

  return true;
}

function getActiveTrapTarget() {
  const preferenceCenter = document.querySelector<HTMLElement>(PREFERENCE_CENTER_SELECTOR);

  if (preferenceCenter && checkIfVisible(preferenceCenter)) return preferenceCenter;

  return document.querySelector<HTMLElement>(OT_CONSENT_CONTAINER);
}

function getVisibleFocusableElements(root: HTMLElement) {
  const candidates = [...root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)];
  const visibleCandidates = candidates.filter(checkIfVisible);

  if (checkIfPreferenceCenterTrapTarget(root)) return visibleCandidates;

  return getBannerFocusableElements({ root, visibleCandidates });
}

function getBannerFocusableElements(params: GetBannerFocusableElementsParams) {
  const { root, visibleCandidates } = params;

  const closeButton = root.querySelector<HTMLElement>(BANNER_CLOSE_BUTTON_SELECTOR);

  if (!closeButton || !checkIfVisible(closeButton)) return visibleCandidates;

  return [closeButton, ...visibleCandidates.filter((candidate) => candidate !== closeButton)];
}

function focusEdgeElement(params: FocusEdgeElementParams) {
  const { event, firstFocusable, lastFocusable } = params;

  const element = event.shiftKey ? lastFocusable : firstFocusable;

  element.focus();
}

function handlePreferenceCenterTabKeyDown(params: HandlePreferenceCenterTabKeyDownParams) {
  const { activeElement, event, firstFocusable, focusableElements, lastFocusable } = params;

  const activeElementIndex = focusableElements.indexOf(activeElement);

  if (activeElementIndex === -1) return;

  if (event.shiftKey && activeElement === firstFocusable) {
    event.preventDefault();
    lastFocusable.focus();

    return;
  }

  if (!event.shiftKey && activeElement === lastFocusable) {
    event.preventDefault();
    firstFocusable.focus();
  }
}

function getNextFocusableElement(params: GetNextFocusableElementParams) {
  const { activeElement, firstFocusable, focusableElements, isShiftKeyPressed, lastFocusable } =
    params;

  const activeElementIndex = focusableElements.indexOf(activeElement);

  if (activeElementIndex === -1) {
    return isShiftKeyPressed ? lastFocusable : firstFocusable;
  }

  const nextElementIndex = isShiftKeyPressed ? activeElementIndex - 1 : activeElementIndex + 1;

  if (nextElementIndex < 0) {
    return lastFocusable;
  }

  if (nextElementIndex >= focusableElements.length) {
    return firstFocusable;
  }

  return focusableElements[nextElementIndex];
}

function getVisiblePreferenceCenterCloseButton() {
  const closeButton = document.querySelector<HTMLElement>(`#${PREFERENCE_CENTER_CLOSE_BUTTON_ID}`);

  if (!closeButton) return;

  const preferenceCenter = closeButton.closest<HTMLElement>(PREFERENCE_CENTER_SELECTOR);

  if (!preferenceCenter) return;

  const isPreferenceCenterHidden = checkIfPreferenceCenterHidden({ closeButton, preferenceCenter });

  if (isPreferenceCenterHidden) return;

  return closeButton;
}

function restoreWhenConsentSurfaceCloses(trapTarget: HTMLElement) {
  consentSurfaceState.restoreObserver?.disconnect();

  if (!checkIfVisible(trapTarget)) {
    restoreClosedConsentSurface();

    return;
  }

  const observer = new MutationObserver(() => {
    restoreIfConsentSurfaceClosed({ observer, trapTarget });
  });

  consentSurfaceState.restoreObserver = observer;

  observeConsentSurfaceClose({ observer, trapTarget });
}

function observeConsentSurfaceClose(params: ObserveConsentSurfaceCloseParams) {
  const { observer, trapTarget } = params;

  observer.observe(trapTarget, CONSENT_SURFACE_OBSERVER_OPTIONS);

  observeConsentSurfaceParentClose({ observer, trapTarget });
}

function observeConsentSurfaceParentClose(params: ObserveConsentSurfaceCloseParams) {
  const { observer, trapTarget } = params;

  const parentElement = trapTarget.parentElement;

  if (!parentElement) return;

  observer.observe(parentElement, CONSENT_SURFACE_PARENT_OBSERVER_OPTIONS);
}

function restoreIfConsentSurfaceClosed(params: RestoreIfConsentSurfaceClosedParams) {
  const { observer, trapTarget } = params;

  if (checkIfVisible(trapTarget)) return;

  observer.disconnect();
  restoreClosedConsentSurfaceOnNextFrame();
}

function restoreClosedConsentSurfaceOnNextFrame() {
  requestAnimationFrame(() => {
    restoreClosedConsentSurface();
  });
}

function restoreClosedConsentSurface() {
  consentSurfaceState.restoreObserver = undefined;
  restoreBackgroundContent();
  restorePreviousFocus();
}

function checkIfVisible(element: HTMLElement) {
  const style = getComputedStyle(element);

  if (style.display === 'none' || style.visibility === 'hidden') {
    return false;
  }

  const rect = element.getBoundingClientRect();

  return rect.width > 0 && rect.height > 0;
}

function rememberFocusedElement(element: Element | null) {
  if (!(element instanceof HTMLElement)) return;

  const trapTarget = document.querySelector<HTMLElement>(OT_CONSENT_CONTAINER);

  if (trapTarget?.contains(element)) return;

  consentSurfaceState.lastFocusedBeforeConsent = element;
}

function applyBackgroundContentInert() {
  const trapTarget = document.querySelector<HTMLElement>(OT_CONSENT_CONTAINER);

  if (!trapTarget) return;

  const backgroundElements = getBackgroundElements(trapTarget);

  backgroundElements.forEach(applyBackgroundElementInert);
}

function getBackgroundElements(trapTarget: HTMLElement): HTMLElement[] {
  return getBackgroundElementsFromAncestor({
    currentElement: trapTarget,
    parentElement: trapTarget.parentElement,
  });
}

function getBackgroundElementsFromAncestor(
  params: GetBackgroundElementsFromAncestorParams,
): HTMLElement[] {
  const { currentElement, parentElement } = params;

  if (!parentElement) return [];

  const siblingElements = [...parentElement.children].filter((element): element is HTMLElement =>
    checkIfBackgroundElement({ currentElement, element }),
  );

  if (parentElement === document.body) return siblingElements;

  return [
    ...siblingElements,
    ...getBackgroundElementsFromAncestor({
      currentElement: parentElement,
      parentElement: parentElement.parentElement,
    }),
  ];
}

function applyBackgroundElementInert(element: HTMLElement) {
  rememberBackgroundElementState(element);

  inertBackgroundElements.add(element);

  element.setAttribute('aria-hidden', 'true');
  element.setAttribute('inert', '');
}

function rememberBackgroundElementState(element: HTMLElement) {
  if (backgroundElementStates.has(element)) return;

  backgroundElementStates.set(element, {
    ariaHidden: element.getAttribute('aria-hidden'),
    inert: element.hasAttribute('inert'),
  });
}

function restoreBackgroundContent() {
  inertBackgroundElements.forEach((element) => {
    const state = backgroundElementStates.get(element);

    if (!state) return;

    restoreAriaHidden({ element, value: state.ariaHidden });
    restoreInert({ element, value: state.inert });

    backgroundElementStates.delete(element);
  });

  inertBackgroundElements.clear();
}

function restorePreviousFocus() {
  if (!consentSurfaceState.lastFocusedBeforeConsent?.isConnected) return;

  consentSurfaceState.lastFocusedBeforeConsent.focus();
  consentSurfaceState.lastFocusedBeforeConsent = undefined;
}

function restoreAriaHidden(params: RestoreAriaHiddenParams) {
  const { element, value } = params;

  if (value === null) {
    element.removeAttribute('aria-hidden');

    return;
  }

  element.setAttribute('aria-hidden', value);
}

function restoreInert(params: RestoreInertParams) {
  const { element, value } = params;

  if (!value) {
    element.removeAttribute('inert');

    return;
  }

  element.setAttribute('inert', '');
}

function checkIfBackgroundElement(params: CheckIfBackgroundElementParams) {
  const { currentElement, element } = params;

  if (!(element instanceof HTMLElement)) {
    return false;
  }

  if (element === currentElement) {
    return false;
  }

  if (element.tagName === 'SCRIPT' || element.tagName === 'STYLE') {
    return false;
  }

  return true;
}

function checkIfPreferenceCenterCloseButton(element: HTMLElement) {
  return element.id === PREFERENCE_CENTER_CLOSE_BUTTON_ID;
}

function checkIfPreferenceCenterTrapTarget(element: HTMLElement) {
  return element.matches(PREFERENCE_CENTER_SELECTOR);
}

function checkIfPreferenceCenterHidden(params: CheckIfPreferenceCenterHiddenParams) {
  const { closeButton, preferenceCenter } = params;

  return (
    preferenceCenter.classList.contains('ot-hide') ||
    (!checkIfVisible(preferenceCenter) && !checkIfVisible(closeButton))
  );
}

function getOneTrust() {
  return (globalThis as GlobalOneTrust).OneTrust;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const OT_CONSENT_CONTAINER = '#onetrust-consent-sdk';
const PREFERENCE_CENTER_SELECTOR = '#onetrust-pc-sdk';
const PREFERENCE_CENTER_CLOSE_BUTTON_ID = 'close-pc-btn-handler';
const TRAP_APPLIED_ATTRIBUTE = 'data-keyboard-trap-applied';
const BANNER_CLOSE_BUTTON_SELECTOR = '#onetrust-banner-sdk .onetrust-close-btn-handler';

const CONSENT_SURFACE_OBSERVER_OPTIONS = {
  attributes: true,
  attributeFilter: ['aria-hidden', 'class', 'hidden', 'style'],
  childList: true,
  subtree: true,
};

const CONSENT_SURFACE_PARENT_OBSERVER_OPTIONS = { childList: true };

const CLOSE_BUTTON_SELECTOR = [
  '.onetrust-close-btn-handler',
  '#accept-recommended-btn-handler',
  '#close-pc-btn-handler',
  '.save-preference-btn-handler',
].join(',');

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const backgroundElementStates = new WeakMap<HTMLElement, BackgroundElementState>();
const inertBackgroundElements = new Set<HTMLElement>();
const consentSurfaceState: ConsentSurfaceState = { lastFocusedBeforeConsent: undefined };

// ─── Types ───────────────────────────────────────────────────────────────────

type BackgroundElementState = { ariaHidden: string | null; inert: boolean };

type CheckIfBackgroundElementParams = {
  currentElement: HTMLElement;
  element: Element;
};

type CheckIfPreferenceCenterHiddenParams = {
  closeButton: HTMLElement;
  preferenceCenter: HTMLElement;
};

type FocusEdgeElementParams = {
  event: KeyboardEvent;
  firstFocusable: HTMLElement;
  lastFocusable: HTMLElement;
};

type GetBannerFocusableElementsParams = {
  root: HTMLElement;
  visibleCandidates: HTMLElement[];
};

type HandlePreferenceCenterTabKeyDownParams = {
  activeElement: HTMLElement;
  event: KeyboardEvent;
  firstFocusable: HTMLElement;
  focusableElements: HTMLElement[];
  lastFocusable: HTMLElement;
};

type GetNextFocusableElementParams = {
  activeElement: HTMLElement;
  firstFocusable: HTMLElement;
  focusableElements: HTMLElement[];
  isShiftKeyPressed: boolean;
  lastFocusable: HTMLElement;
};

type GetBackgroundElementsFromAncestorParams = {
  currentElement: HTMLElement;
  parentElement: HTMLElement | null;
};

type ObserveConsentSurfaceCloseParams = {
  observer: MutationObserver;
  trapTarget: HTMLElement;
};

type RestoreAriaHiddenParams = {
  element: HTMLElement;
  value: string | null;
};

type RestoreIfConsentSurfaceClosedParams = {
  observer: MutationObserver;
  trapTarget: HTMLElement;
};

type RestoreInertParams = {
  element: HTMLElement;
  value: boolean;
};

type ConsentSurfaceState = {
  hasDocumentListeners?: boolean;
  lastFocusedBeforeConsent?: HTMLElement;
  restoreObserver?: MutationObserver;
};

type GlobalOneTrust = {
  OneTrust?: {
    Close?: () => void;
  };
};
